<p-toast position="top-center"></p-toast>
@if (message && !release) {
  <app-page-shell>
    <app-page-data-panel title="Order Release" subtitle="The requested release could not be opened.">
      <div class="empty-state">{{ message }}</div>
    </app-page-data-panel>
  </app-page-shell>
}

<app-page-shell *ngIf="release as row">
  <div class="release-page">
    <app-page-detail-hero [auditStamp]="{ createdAt: row.createdAt, updatedAt: row.updatedAt }"
      eyebrow="Order Release"
      [title]="row.releaseNumber || 'Order Release'"
      [subtitle]="heroSubtitle()"
      icon="pi pi-list-check">
      <div tags class="release-hero-tags">
        <div class="release-hero-status" [ngClass]="releaseHeroStatusClass()">
          <span><i class="pi pi-circle-fill"></i> Release Status</span>
          <strong>{{ formatStatusLabel(row.releaseStatus) }}</strong>
        </div>
        <div class="release-hero-meta-tags">
          <p-tag [value]="row.validationStatus || 'READY'" [severity]="statusSeverity(row.validationStatus)"></p-tag>
          <p-tag [value]="row.sourceType || 'SOURCE'" severity="info"></p-tag>
          <p-tag [value]="roleLabel()" [severity]="roleSeverity()"></p-tag>
          @if (row.customerTrackingNumber) {
            <p-tag [value]="row.customerTrackingNumber" severity="success"></p-tag>
          }
        </div>
      </div>
      <div actions>
        <p-button label="Release Pool" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/transport-planning/releases"></p-button>
      </div>
    </app-page-detail-hero>

    @if (row.cancelledAt) {
      <div class="cancellation-audit">Cancelled {{ row.cancelledAt | date:'medium' }} by {{ row.cancelledByActor?.name || 'Unknown actor' }}. Reason: {{ row.cancellationReason || 'Not recorded' }}</div>
    }

    <app-page-metric-strip [metrics]="detailMetrics()"></app-page-metric-strip>

    <div class="release-detail-layout">
      <main class="release-detail-main">
        <app-page-data-panel
      title="Release Workspace"
      subtitle="Route, demand, planning, tracking, documents, and audit in one operating view."
      [resultText]="trackingNumber()">
      <section class="linked-records" aria-labelledby="linked-records-title">
        <div class="linked-records-heading">
          <h3 id="linked-records-title">Linked records</h3>
          <p>Commercial context, source records, and transport execution.</p>
        </div>
        <div class="linked-record-groups">
          <section class="linked-record-group" aria-label="Commercial context">
            <h4><i class="pi pi-building"></i> Commercial</h4>
            <dl>
              <div><dt>Customer</dt><dd [class.missing]="!hasCustomerContext(row)">{{ customerLabel(row) }}</dd></div>
              @if (accountLabel(row)) {
                <div><dt>Account</dt><dd>{{ accountLabel(row) }}</dd></div>
              }
              @if (row.crmClientContactId) {
                <div><dt>Contact</dt><dd>CRM Contact #{{ row.crmClientContactId }}</dd></div>
              }
              @if (row.commercialPartySource) {
                <div><dt>Party source</dt><dd>{{ row.commercialPartySource }}</dd></div>
              }
            </dl>
          </section>

          <section class="linked-record-group" aria-label="Source and project records">
            <h4><i class="pi pi-bookmark"></i> Source &amp; project</h4>
            <dl>
              @if (row.sourceNumber || row.sourceId) {
                <div><dt>Source record</dt><dd>
                  @if (canViewSource()) {
                    <button type="button" class="linked-record-link" (click)="viewSource()">{{ row.sourceNumber || row.sourceId }}</button>
                  } @else {
                    {{ row.sourceNumber || row.sourceId }}
                  }
                </dd></div>
              }
              @if (hasLogisticsProject()) {
                <div><dt>Logistics project</dt><dd>
                  @if (row.logisticsProjectId) {
                    <a [routerLink]="logisticsProjectRoute()">{{ logisticsProjectLabel() }}</a>
                  } @else {
                    {{ logisticsProjectLabel() }}
                  }
                </dd></div>
              }
              @if (row.parentReleaseId) {
                <div><dt>Parent release</dt><dd><a [routerLink]="['/transport-planning/releases', row.parentReleaseId]">Release #{{ row.parentReleaseId }}</a></dd></div>
              }
              @if (row.crossDockOrchestrationId) {
                <div><dt>Orchestration</dt><dd><a [routerLink]="orchestrationRoute()">{{ orchestrationTitle() }}</a></dd></div>
              }
            </dl>
          </section>

          <section class="linked-record-group" aria-label="Execution records">
            <h4><i class="pi pi-truck"></i> Execution</h4>
            <dl>
              @if (row.plannedShipmentId) {
                <div><dt>Shipment</dt><dd><a [routerLink]="plannedShipmentRoute()">{{ primaryShipmentLabel() }}</a></dd></div>
              }
              @for (delivery of tracking?.deliveries || []; track delivery.deliveryId || $index) {
                <div><dt>Delivery</dt><dd>
                  @if (delivery.deliveryId) {
                    <a [routerLink]="['/shipment/delivery/view', delivery.deliveryId]">{{ delivery.deliveryNumber || ('Delivery #' + delivery.deliveryId) }}</a>
                  } @else {
                    {{ delivery.deliveryNumber || 'Delivery' }}
                  }
                </dd></div>
              }
              @if (row.customerTrackingNumber) {
                <div><dt>Public tracking</dt><dd><a [href]="publicTrackingUrl()" target="_blank" rel="noopener">Open tracking <i class="pi pi-external-link"></i></a></dd></div>
              }
            </dl>
            @if (!row.plannedShipmentId && !tracking?.deliveries?.length && !row.customerTrackingNumber) {
              <p class="linked-record-empty">No execution records linked yet.</p>
            }
          </section>
        </div>
      </section>

      <p-tabs [value]="activeTabIndex" (valueChange)="onTabChange($event)" styleClass="release-workspace-tabs">
        <p-tablist>
          <p-tab [value]="0">
            <span class="workspace-tab"><i class="fa-solid fa-gauge-high"></i><span>Overview</span></span>
          </p-tab>
          <p-tab [value]="1">
            <span class="workspace-tab"><i class="pi pi-list-check"></i><span>Lines</span><span class="workspace-badge info">{{ lineCount() }}</span></span>
          </p-tab>
          <p-tab [value]="2">
            <span class="workspace-tab"><i class="fa-solid fa-route"></i><span>Planning</span></span>
          </p-tab>
          <p-tab [value]="3">
            <span class="workspace-tab"><i class="pi pi-qrcode"></i><span>Tracking</span><span class="workspace-badge success">{{ trackingEvents().length }}</span></span>
          </p-tab>
          @if (row.crossDockOrchestrationId) {
            <p-tab [value]="4">
              <span class="workspace-tab"><i class="fa-solid fa-network-wired"></i><span>Orchestration</span><span class="workspace-badge info">{{ orchestrationLegs().length || row.legCount || 0 }}</span></span>
            </p-tab>
          }
        </p-tablist>

        <p-tabpanels>
          <p-tabpanel [value]="0">
            <section class="workspace-section">
              <div class="route-board">
                <div class="route-endpoint">
                  <span class="endpoint-kicker">Pickup</span>
                  <h3>{{ row.originName || 'Start place missing' }}</h3>
                  <div class="endpoint-actions">
                    @if (mapUrl(row.originLatitude, row.originLongitude); as originUrl) {
                      <a class="endpoint-address" [href]="originUrl" target="_blank">{{ row.originLatitude }}, {{ row.originLongitude }}</a>
                    } @else {
                      <span class="endpoint-address muted">Coordinates not set</span>
                    }
                    @if (row.originLocationId) {
                      <button type="button" class="endpoint-edit-link change" (click)="openLocationAssignment('origin')">
                        <i class="pi pi-sync"></i>
                        <span>Change location</span>
                      </button>
                      <a class="endpoint-edit-link" [routerLink]="locationEditRoute(row.originLocationId)" pTooltip="Edit master location record">
                        <i class="pi pi-pencil"></i>
                        <span>Edit master</span>
                      </a>
                    } @else {
                      <button type="button" class="endpoint-edit-link add" (click)="openLocationAssignment('origin')">
                        <i class="pi pi-plus"></i>
                        <span>Add pickup location</span>
                      </button>
                    }
                  </div>
                </div>
                <div class="route-connector">
                  <span></span>
                  <i class="fa-solid fa-truck-fast"></i>
                  <span></span>
                </div>
                <div class="route-endpoint destination">
                  <span class="endpoint-kicker">Dropoff</span>
                  <h3>{{ row.destinationName || 'End place missing' }}</h3>
                  <div class="endpoint-actions">
                    @if (mapUrl(row.destinationLatitude, row.destinationLongitude); as destinationUrl) {
                      <a class="endpoint-address" [href]="destinationUrl" target="_blank">{{ row.destinationLatitude }}, {{ row.destinationLongitude }}</a>
                    } @else {
                      <span class="endpoint-address muted">Coordinates not set</span>
                    }
                    @if (row.destinationLocationId) {
                      <button type="button" class="endpoint-edit-link change" (click)="openLocationAssignment('destination')">
                        <i class="pi pi-sync"></i>
                        <span>Change location</span>
                      </button>
                      <a class="endpoint-edit-link" [routerLink]="locationEditRoute(row.destinationLocationId)" pTooltip="Edit master location record">
                        <i class="pi pi-pencil"></i>
                        <span>Edit master</span>
                      </a>
                    } @else {
                      <button type="button" class="endpoint-edit-link add" (click)="openLocationAssignment('destination')">
                        <i class="pi pi-plus"></i>
                        <span>Add dropoff location</span>
                      </button>
                    }
                  </div>
                </div>
              </div>

              <div class="route-summary-grid">
                <div><span>Source</span><strong>{{ row.sourceType || '-' }}</strong></div>
                <div><span>Role</span><strong>{{ roleLabel() }}</strong></div>
                <div><span>Priority</span><strong>{{ row.priority || 'NORMAL' }}</strong></div>
                <div><span>Lines</span><strong>{{ lineCount() }}</strong></div>
                <div><span>Units</span><strong>{{ formatAmount(totalUnits()) }}</strong></div>
                <div><span>Pallets</span><strong>{{ formatAmount(totalPallets()) }}</strong></div>
              </div>

              @if (crossDockLegs().length) {
                <div class="info-block">
                  <div class="block-heading"><i class="fa-solid fa-route"></i><span>Cross-Dock Route Legs</span></div>
                  <div class="leg-chain">
                    @for (leg of crossDockLegs(); track leg.releaseId || $index) {
                      <article class="leg-card">
                        <div>
                          <p-tag [value]="legTitle(leg)" severity="info"></p-tag>
                          <strong>{{ leg.originName || 'Origin' }} to {{ leg.destinationName || 'Destination' }}</strong>
                          <small>{{ leg.releaseNumber || '-' }} / {{ leg.customerTrackingNumber || '-' }}</small>
                        </div>
                        <div>
                          <span>{{ legLoadSummary(leg) }}</span>
                          <small>{{ leg.plannedShipmentId ? ('Shipment #' + leg.plannedShipmentId) : (leg.releaseStatus || 'Ready') }}</small>
                        </div>
                      </article>
                    }
                  </div>
                </div>
              }

              <div class="overview-grid">
                <div class="info-block">
                  <div class="block-heading"><i class="pi pi-info-circle"></i><span>Release Header</span></div>
                  <dl>
                    <div><dt>Release #</dt><dd>{{ row.releaseNumber || '-' }}</dd></div>
                    <div><dt>Role</dt><dd>{{ roleLabel() }}</dd></div>
                    <div>
                      <dt>Logistics Project</dt>
                      <dd>
                        @if (hasLogisticsProject()) {
                          <a class="details-link" [routerLink]="logisticsProjectRoute()">{{ logisticsProjectLabel() }}</a>
                        } @else {
                          -
                        }
                      </dd>
                    </div>
                    <div><dt>Parent Release</dt><dd>{{ row.parentReleaseId || '-' }}</dd></div>
                    <div><dt>Depends On</dt><dd>{{ row.dependsOnReleaseId || '-' }}</dd></div>
                    <div><dt>Tracking #</dt><dd>{{ trackingNumber() }}</dd></div>
                    <div><dt>Tracking Source</dt><dd>{{ row.customerTrackingNumberSource || '-' }}</dd></div>
                    <div><dt>Source #</dt><dd>{{ row.sourceNumber || row.sourceId || '-' }}</dd></div>
                    <div><dt>CRM Client</dt><dd>{{ customerLabel(row) }}</dd></div>
                    <div><dt>CRM Account</dt><dd>{{ accountLabel(row) || '-' }}</dd></div>
                    <div><dt>Legacy Customer</dt><dd>{{ row.customerName || row.customerId || '-' }}</dd></div>
                    <div><dt>Legacy Company</dt><dd>{{ row.companyName || row.companyId || '-' }}</dd></div>
                  </dl>
                </div>

                <div class="info-block">
                  <div class="block-heading"><i class="pi pi-calendar-clock"></i><span>Time Windows</span></div>
                  <dl>
                    <div><dt>Pickup Start</dt><dd>{{ formatDateTime(row.pickupWindowStart) }}</dd></div>
                    <div><dt>Pickup End</dt><dd>{{ formatDateTime(row.pickupWindowEnd) }}</dd></div>
                    <div><dt>Delivery Start</dt><dd>{{ formatDateTime(row.deliveryWindowStart) }}</dd></div>
                    <div><dt>Delivery End</dt><dd>{{ formatDateTime(row.deliveryWindowEnd) }}</dd></div>
                  </dl>
                </div>

                <div class="info-block">
                  <div class="block-heading"><i class="pi pi-shield"></i><span>Readiness</span></div>
                  <div class="status-stack">
                    <p-tag [value]="row.releaseStatus || 'DRAFT'" [severity]="statusSeverity(row.releaseStatus)"></p-tag>
                    <p-tag [value]="row.validationStatus || 'READY'" [severity]="statusSeverity(row.validationStatus)"></p-tag>
                  </div>
                  <p class="empty-copy">{{ row.validationMessage || 'No validation issues for this release.' }}</p>
                </div>

                <div class="info-block">
                  <div class="block-heading"><i class="pi pi-link"></i><span>Source</span></div>
                  <dl>
                    <div><dt>Source Type</dt><dd>{{ row.sourceType || '-' }}</dd></div>
                    <div><dt>Source Reference</dt><dd>{{ row.sourceNumber || row.sourceId || '-' }}</dd></div>
                    <div>
                      <dt>Shipment</dt>
                      <dd>
                        @if (row.plannedShipmentId) {
                          <a class="details-link" [routerLink]="plannedShipmentRoute()">{{ primaryShipmentLabel() }}</a>
                        } @else {
                          Not created
                        }
                      </dd>
                    </div>
                  </dl>
                </div>

                <div class="info-block">
                  <div class="block-heading"><i class="pi pi-info-circle"></i><span>Validation Notes</span></div>
                  <div class="notes-panel">
                    <span>{{ row.validationStatus || 'READY' }}</span>
                    <p>{{ row.validationMessage || 'No validation notes for this release.' }}</p>
                  </div>
                </div>
              </div>
            </section>
          </p-tabpanel>

          <p-tabpanel [value]="1">
            <section class="workspace-section">
              <div class="section-toolbar">
                <div>
                  <h3>Item Demand Lines</h3>
                  <p>{{ lineCount() }} line{{ lineCount() === 1 ? '' : 's' }} imported from POSM, sales order, or manual release demand.</p>
                </div>
              </div>
              <div class="release-line-ledger">
                @if (getLines().length) {
                  <div class="release-line-list">
                    @for (line of getLines(); track line.releaseLineId || $index) {
                      <article class="release-line-card">
                        <div class="release-line-main">
                          <div>
                            <span>Demand Line</span>
                            <strong>{{ lineTitle(line) }}</strong>
                            <small>Line {{ $index + 1 }}{{ lineDescription(line) !== '-' ? (' | ' + lineDescription(line)) : '' }}</small>
                          </div>
                          <div class="release-line-tags">
                            <p-tag [value]="line.sourceLineType || 'LINE'" severity="secondary"></p-tag>
                            @if (line.uom) {
                              <p-tag [value]="line.uom" severity="info"></p-tag>
                            }
                          </div>
                        </div>

                        <div class="release-line-grid">
                          <div>
                            <span>Quantity</span>
                            <strong>{{ lineQuantity(line) }}</strong>
                            <small>{{ line.weightKg ? (formatAmount(line.weightKg) + ' kg') : 'Weight pending' }}</small>
                          </div>
                          <div>
                            <span>Warehouse Item</span>
                            @if (line.inventoryItemId) {
                              <a class="details-link" [routerLink]="inventoryItemRoute(line)">{{ inventoryItemLinkLabel(line) }}</a>
                            } @else {
                              <strong>{{ inventoryItemLinkLabel(line) }}</strong>
                            }
                            <small>{{ inventoryCodeDetails(line) }}</small>
                          </div>
                          <div>
                            <span>Item Name</span>
                            <strong>{{ inventoryItemTitle(line) }}</strong>
                            <small>{{ inventoryPhysicalDetails(line) || 'Physical details pending' }}</small>
                          </div>
                          <div>
                            <span>Source</span>
                            <strong>{{ lineSource(line) }}</strong>
                            <small>{{ line.sourceLineType || 'Source line reference' }}</small>
                          </div>
                          <div>
                            <span>Source Warehouse</span>
                            <strong>{{ sourceWarehouseLabel(line) }}</strong>
                            <small>{{ zoneConditionLabel(line) }}</small>
                          </div>
                          <div>
                            <span>Item Size / Handling</span>
                            <strong>{{ line.itemSize || '-' }}</strong>
                            <small>{{ line.actionRequired || 'No handling action' }}</small>
                          </div>
                        </div>
                      </article>
                    }
                  </div>
                } @else {
                  <div class="empty-state compact-empty release-line-empty">
                    <i class="fa-solid fa-box-open"></i>
                    <strong>No order release lines</strong>
                    <span>This release does not have any item demand lines yet.</span>
                  </div>
                }
              </div>
              <div class="route-summary-grid totals-grid">
                <div><span>Quantity</span><strong>{{ formatAmount(totalQuantity()) }} {{ sharedUom() }}</strong></div>
                <div><span>Units</span><strong>{{ formatAmount(totalUnits()) }}</strong></div>
                <div><span>Pallets</span><strong>{{ formatAmount(totalPallets()) }}</strong></div>
                <div><span>Weight</span><strong>{{ formatAmount(totalWeight()) }} kg</strong></div>
                <div><span>Volume</span><strong>{{ formatAmount(totalVolume()) }} cbm</strong></div>
              </div>
            </section>
          </p-tabpanel>

          <p-tabpanel [value]="2">
            <section class="workspace-section">
              <div class="dispatch-board">
                <div><span>Release</span><strong>{{ formatStatusLabel(row.releaseStatus) }}</strong></div>
                <div><span>Role</span><strong>{{ roleLabel() }}</strong></div>
                <div><span>Validation</span><strong>{{ row.validationStatus || 'READY' }}</strong></div>
                <div><span>Shipment</span><strong>{{ plannedShipmentStatus() }}</strong></div>
                <div><span>Delivery</span><strong>{{ primaryDeliveryStatus() }}</strong></div>
                <div><span>Manpower</span><strong>{{ row.manpowerCount || 0 }} + {{ row.helperCount || 0 }}</strong></div>
              </div>

              <div class="overview-grid two-column">
                <div class="info-block">
                  <div class="block-heading"><i class="pi pi-sliders-h"></i><span>Truck Assignment</span></div>
                  <dl>
                    <div><dt>Vehicle Type</dt><dd>{{ planningVehicleType() }}</dd></div>
                    <div><dt>Route Template</dt><dd>{{ row.routeTemplateCode || row.routeTemplateId || '-' }}</dd></div>
                    <div><dt>Assignment Rule</dt><dd>{{ row.assignmentRuleCode || row.assignmentRuleId || '-' }}</dd></div>
                    <div><dt>Assignment Mode</dt><dd>{{ planningAssignmentModeLabel() }}</dd></div>
                    <div><dt>Driver</dt><dd>{{ planningDriverName() }}</dd></div>
                    <div><dt>Vehicle</dt><dd>{{ planningVehicleLabel() }}</dd></div>
                </dl>
                </div>

                <div class="info-block">
                  <div class="block-heading"><i class="pi pi-truck"></i><span>Shipment and Delivery</span></div>
                  <dl>
                    <div>
                      <dt>Shipment</dt>
                      <dd>
                        @if (row.plannedShipmentId) {
                          <a class="details-link" [routerLink]="plannedShipmentRoute()">{{ primaryShipmentLabel() }}</a>
                        } @else {
                          Not created
                        }
                      </dd>
                    </div>
                    <div><dt>Shipment Status</dt><dd>{{ plannedShipmentStatus() }}</dd></div>
                    <div>
                      <dt>Delivery</dt>
                      <dd>
                        @if (hasPrimaryDelivery()) {
                          <a class="details-link" [routerLink]="primaryDeliveryRoute()">{{ primaryDeliveryLabel() }}</a>
                        } @else {
                          Not created
                        }
                      </dd>
                    </div>
                    <div><dt>Delivery Status</dt><dd>{{ primaryDeliveryStatus() }}</dd></div>
                    <div><dt>Shipment Reference</dt><dd>{{ planningShipmentReference() }}</dd></div>
                    <div><dt>Mobilization</dt><dd>{{ planningMobilizationLabel() }}</dd></div>
                  </dl>
                </div>

                <div class="info-block">
                  <div class="block-heading"><i class="pi pi-exclamation-triangle"></i><span>Overrides and Booking</span></div>
                  <dl>
                    <div><dt>Override</dt><dd>{{ row.overrideStatus || '-' }}</dd></div>
                    <div><dt>Override Reason</dt><dd>{{ row.overrideReason || 'No override reason' }}</dd></div>
                    <div><dt>Required Tools</dt><dd>{{ row.requiredTools || '-' }}</dd></div>
                    <div>
                      <dt>Project</dt>
                      <dd>
                        @if (hasLogisticsProject()) {
                          <a class="details-link" [routerLink]="logisticsProjectRoute()">{{ logisticsProjectLabel() }}</a>
                        } @else {
                          -
                        }
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </section>
          </p-tabpanel>

          <p-tabpanel [value]="3">
            <section class="workspace-section">
              <div class="overview-grid two-column">
                <div class="info-block">
                  <div class="block-heading"><i class="pi pi-qrcode"></i><span>Customer Tracking</span></div>
                  <dl>
                    <div><dt>Tracking Number</dt><dd>{{ trackingNumber() }}</dd></div>
                    <div><dt>Cross-Dock Legs</dt><dd>{{ crossDockLegs().length || '-' }}</dd></div>
                    <div><dt>Number Source</dt><dd>{{ row.customerTrackingNumberSource || '-' }}</dd></div>
                    <div><dt>Shipments</dt><dd>{{ shipmentCount() }}</dd></div>
                    <div><dt>Deliveries</dt><dd>{{ deliveryCount() }}</dd></div>
                  </dl>
                  <p-button label="Open Public Page" icon="pi pi-external-link" [disabled]="!canViewPublicTracking()" (onClick)="openPublicTracking()"></p-button>
                </div>

                <div class="info-block">
                  <div class="block-heading"><i class="pi pi-history"></i><span>Tracking Events</span></div>
                  @if (trackingEvents().length) {
                    <div class="timeline">
                      @for (event of trackingEvents(); track event.eventAt || $index) {
                        <div class="timeline-item">
                          <div class="timeline-dot"><i [class]="eventIcon(event)"></i></div>
                          <div>
                            <strong>{{ event.title || event.eventType || 'Update' }}</strong>
                            <p>{{ event.description || event.status || event.locationName || '-' }}</p>
                            <small>{{ formatDateTime(event.eventAt) }}{{ event.deliveryNumber ? ' | ' + event.deliveryNumber : '' }}{{ event.locationName ? ' | ' + event.locationName : '' }}</small>
                          </div>
                        </div>
                      }
                    </div>
                  } @else {
                    <p class="empty-copy">No public tracking events have been recorded yet.</p>
                  }
                </div>
              </div>

              <div class="info-block">
                <div class="block-heading"><i class="pi pi-paperclip"></i><span>Waybill, Evidence and POD</span></div>
                @if (trackingDocuments().length) {
                  <div class="document-grid">
                    @for (document of trackingDocuments(); track document.url || $index) {
                      <article class="document-row">
                        <div>
                          <p-tag [value]="document.documentType || 'DOCUMENT'" [severity]="documentSeverity(document.documentType)"></p-tag>
                          <strong>{{ document.name || document.documentType || 'Document' }}</strong>
                          <small>{{ formatDateTime(document.capturedAt) }} | {{ document.source || '-' }}</small>
                        </div>
                        <button type="button" class="icon-button" [disabled]="!document.url" (click)="openDocument(document)" aria-label="Open document">
                          <i class="pi pi-external-link"></i>
                        </button>
                      </article>
                    }
                  </div>
                } @else {
                  <p class="empty-copy">No waybill, evidence, or POD files have been linked yet.</p>
                }
              </div>
            </section>
          </p-tabpanel>

          @if (row.crossDockOrchestrationId) {
            <p-tabpanel [value]="4">
              <section class="workspace-section">
                <div class="section-toolbar">
                  <div>
                    <h3>Cross-Dock Orchestration</h3>
                    <p>{{ orchestrationTitle() }} coordinates warehouse transfers, gates, and final dispatch for this release tree.</p>
                  </div>
                  <p-button label="Open Orchestration" icon="pi pi-arrow-right" [routerLink]="orchestrationRoute()"></p-button>
                </div>

                <div class="dispatch-board">
                  <div><span>Orchestration ID</span><strong>{{ row.crossDockOrchestrationId }}</strong></div>
                  <div><span>Number</span><strong>{{ orchestrationTitle() }}</strong></div>
                  <div><span>Status</span><strong>{{ orchestration?.status || row.releaseStatus || 'PLANNED' }}</strong></div>
                  <div><span>Legs</span><strong>{{ orchestrationLegs().length || row.legCount || 0 }}</strong></div>
                  <div><span>Transfer</span><strong>{{ row.linkedWarehouseTransferId || '-' }}</strong></div>
                  <div><span>Dispatch</span><strong>{{ row.linkedWarehouseDispatchId || '-' }}</strong></div>
                </div>

                @if (orchestrationLegs().length) {
                  <div class="leg-chain">
                    @for (leg of orchestrationLegs(); track leg.id || leg.legSequence || $index) {
                      <article class="leg-card">
                        <div>
                          <p-tag [value]="'Leg ' + (leg.legSequence || '-')" severity="info"></p-tag>
                          <strong>{{ leg.originName || 'Origin' }} to {{ leg.destinationName || 'Destination' }}</strong>
                          <small>{{ leg.legType || '-' }} / {{ leg.status || 'PLANNED' }}</small>
                        </div>
                        <div>
                          <span>{{ leg.transferNumber || leg.dispatchNumber || '-' }}</span>
                          <small>{{ leg.gatedByTransferId ? ('Gated by transfer #' + leg.gatedByTransferId) : 'No inbound gate' }}</small>
                        </div>
                      </article>
                    }
                  </div>
                } @else {
                  <p class="empty-copy">The orchestration record exists, but leg details have not been loaded yet.</p>
                }
              </section>
            </p-tabpanel>
          }
        </p-tabpanels>
      </p-tabs>
    </app-page-data-panel>
    @if (release; as fieldRelease) {
      <app-field-resources-panel
        targetType="TRANSPORT_ORDER_RELEASE"
        [targetId]="fieldRelease.releaseId"
        [targetReference]="fieldRelease.releaseNumber"
        [logisticsProjectId]="fieldRelease.logisticsProjectId"
        [customerCrmClientId]="fieldRelease.crmClientId"
        [locationId]="fieldRelease.originLocationId"
        [locationName]="fieldRelease.originName">
      </app-field-resources-panel>
    }
      </main>

      <aside class="business-action-pane" aria-label="Command Center">
        <header class="business-action-pane__header">
          <span class="business-action-pane__eyebrow">Command Center</span>
          <h2>Release Actions</h2>
          <p>Manage route locations, planning execution, tracking, and release status.</p>
        </header>

        <section class="business-action-group business-action-group--primary">
          <div class="business-action-group__heading">
            <i class="pi pi-map-marker"></i>
            <div>
              <strong>Route &amp; Locations</strong>
              <span>Change or assign pickup and dropoff points</span>
            </div>
          </div>
          <p-button
            [label]="row.originLocationId ? 'Change Pickup Location' : 'Assign Pickup Location'"
            icon="pi pi-map-marker"
            severity="secondary"
            [outlined]="true"
            (onClick)="openLocationAssignment('origin')"></p-button>
          <p-button
            [label]="row.destinationLocationId ? 'Change Dropoff Location' : 'Assign Dropoff Location'"
            icon="pi pi-map-marker"
            severity="secondary"
            [outlined]="true"
            (onClick)="openLocationAssignment('destination')"></p-button>
          @if (row.originLocationId) {
            <p-button
              label="Edit Pickup Master"
              icon="pi pi-pencil"
              severity="secondary"
              [outlined]="true"
              [routerLink]="locationEditRoute(row.originLocationId)"></p-button>
          }
          @if (row.destinationLocationId) {
            <p-button
              label="Edit Dropoff Master"
              icon="pi pi-pencil"
              severity="secondary"
              [outlined]="true"
              [routerLink]="locationEditRoute(row.destinationLocationId)"></p-button>
          }
        </section>

        <section class="business-action-group">
          <div class="business-action-group__heading">
            <i class="fa-solid fa-truck-fast"></i>
            <div>
              <strong>Planning &amp; Execution</strong>
              <span>Dispatch to shipment or build transport plan</span>
            </div>
          </div>
          @if (!row.plannedShipmentId) {
            <p-button
              label="Add to Existing Shipment"
              icon="fa-solid fa-link"
              severity="success"
              [disabled]="!canAddToShipment()"
              (onClick)="addToShipment()"></p-button>
            <p-button
              label="Build Truck Plan"
              icon="fa-solid fa-route"
              severity="secondary"
              [outlined]="true"
              [disabled]="!canBuildTruckPlan()"
              (onClick)="buildTruckPlan()"></p-button>
          } @else {
            <p-button
              label="Open Shipment"
              icon="fa-solid fa-truck"
              severity="info"
              [outlined]="true"
              [routerLink]="plannedShipmentRoute()"></p-button>
            @if (hasPrimaryDelivery()) {
              <p-button
                label="Open Delivery"
                icon="pi pi-send"
                severity="secondary"
                [outlined]="true"
                [routerLink]="primaryDeliveryRoute()"></p-button>
            }
          }
        </section>

        <section class="business-action-group">
          <div class="business-action-group__heading">
            <i class="pi pi-qrcode"></i>
            <div>
              <strong>Customer Tracking</strong>
              <span>Live tracking page and client sharing</span>
            </div>
          </div>
          <p-button
            label="Open Public Tracking"
            icon="pi pi-external-link"
            severity="secondary"
            [outlined]="true"
            [disabled]="!canViewPublicTracking()"
            (onClick)="openPublicTracking()"></p-button>
          <p-button
            label="Copy Tracking Link"
            icon="pi pi-copy"
            severity="secondary"
            [outlined]="true"
            [disabled]="!canViewPublicTracking()"
            (onClick)="copyPublicTrackingLink()"></p-button>
          <p-button
            label="Share on WhatsApp"
            icon="pi pi-whatsapp"
            severity="success"
            [outlined]="true"
            [disabled]="!canSharePublicTrackingOnWhatsapp()"
            (onClick)="sharePublicTrackingOnWhatsapp()"></p-button>
        </section>

        @if (canCancelRelease()) {
          <section class="business-action-group">
            <div class="business-action-group__heading">
              <i class="pi pi-ban"></i>
              <div>
                <strong>Release Management</strong>
                <span>Cancel this release and its linked work</span>
              </div>
            </div>
            <p-button label="Cancel Release" icon="pi pi-ban" severity="danger" [outlined]="true"
              (onClick)="openCancelDialog()"></p-button>
          </section>
        }
      </aside>
    </div>
  </div>
</app-page-shell>

<p-dialog
  [(visible)]="locationDialogVisible"
  [header]="locationDialogTitle()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '36rem' }"
  [breakpoints]="{ '640px': '94vw' }">
  <div class="location-assign-dialog">
    @if (currentEndpointLocationName()) {
      <div class="current-location-notice">
        <small>Current {{ locationDialogEndpoint === 'origin' ? 'Pickup' : 'Dropoff' }} Location</small>
        <strong>{{ currentEndpointLocationName() }}</strong>
        @if (currentEndpointCoordinates()) {
          <span class="location-notice-coords">{{ currentEndpointCoordinates() }}</span>
        }
      </div>
    }
    <label for="release-location-select">
      {{ isCurrentEndpointAssigned() ? 'Select new location' : 'Existing location' }}
    </label>
    <p-select
      inputId="release-location-select"
      appLazyLocationPicker
      [(ngModel)]="selectedLocation"
      optionLabel="name"
      dataKey="id"
      [filter]="true"
      filterBy="name,externalLocationReference,locationType"
      [showClear]="true"
      appendTo="body"
      placeholder="Select an existing location"
      styleClass="w-full">
      <ng-template #selectedItem let-location>
        <span>{{ locationLabel(location) }}</span>
      </ng-template>
      <ng-template #item let-location>
        <div class="location-option-row">
          <strong>{{ locationLabel(location) }}</strong>
          <small>{{ locationMeta(location) }}</small>
        </div>
      </ng-template>
    </p-select>
    <p class="empty-copy">Select a saved master location, or create a new one if the pickup/dropoff does not exist yet.</p>
  </div>
  <ng-template pTemplate="footer">
    <div class="location-assign-actions">
      <p-button label="Create New Location" icon="pi pi-plus" severity="secondary" [outlined]="true" (onClick)="createLocationForEndpoint()"></p-button>
      <p-button [label]="assignButtonLabel()" icon="pi pi-check" [disabled]="!selectedLocation" [loading]="isAssigningLocation" (onClick)="assignSelectedLocation()"></p-button>
    </div>
  </ng-template>
</p-dialog>
<p-dialog header="Cancel order release family" [(visible)]="cancelDialogVisible" [modal]="true"
  [style]="{ width: 'min(960px, 96vw)' }" [breakpoints]="{ '860px': '96vw' }"
  [draggable]="false" [resizable]="false" styleClass="master-create-dialog release-cancel-dialog">
  <section class="master-dialog-body">
    <aside class="master-dialog-summary">
      <div class="summary-icon"><i class="pi pi-ban"></i></div>
      <div>
        <small>Order release</small>
        <h3>{{ release?.releaseNumber || 'Release family' }}</h3>
        <p>Review every linked release and warehouse document before cancelling. The reason will be recorded with the cancellation.</p>
      </div>
      @if (cancellationImpact; as impact) {
        <div class="master-dialog-facts">
          <div><span>Releases</span><strong>{{ impact.releaseIds.length }}</strong></div>
          <div><span>Transfers</span><strong>{{ impact.transferIds.length }}</strong></div>
          <div><span>Dispatches</span><strong>{{ impact.dispatchIds.length }}</strong></div>
        </div>
      }
    </aside>

    <div class="master-dialog-form">
      <div class="cancel-section-heading">
        <small>Cancellation impact</small>
        <h4>Linked work</h4>
      </div>
      @if (cancellationImpact; as impact) {
        <div class="cancel-impact-list">
          <div class="cancel-impact-group">
            <strong>Release family</strong>
            <div class="cancel-impact-values">
              @for (id of impact.releaseIds; track id) { <span>Release #{{ id }}</span> }
            </div>
          </div>
          @if (impact.transferIds.length) {
            <div class="cancel-impact-group">
              <strong>Warehouse transfers</strong>
              <div class="cancel-impact-values">
                @for (id of impact.transferIds; track id) { <span>Transfer #{{ id }}</span> }
              </div>
            </div>
          }
          @if (impact.dispatchIds.length) {
            <div class="cancel-impact-group">
              <strong>Warehouse dispatches</strong>
              <div class="cancel-impact-values">
                @for (id of impact.dispatchIds; track id) { <span>Dispatch #{{ id }}</span> }
              </div>
            </div>
          }
          @if (!impact.transferIds.length && !impact.dispatchIds.length) {
            <p class="cancel-empty">No warehouse documents are linked.</p>
          }
        </div>
      } @else if (!cancelDialogError) {
        <p class="cancel-empty">Loading linked work...</p>
      }
      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-comment"></i></p-inputGroupAddon>
        <p-iftalabel>
          <textarea id="release-cancellation-reason" pTextarea [(ngModel)]="cancellationReason"
            rows="4" maxlength="500" class="w-full" [attr.aria-describedby]="cancelDialogError ? 'release-cancel-error' : null"></textarea>
          <label for="release-cancellation-reason">Reason for cancellation *</label>
        </p-iftalabel>
      </p-inputGroup>
      @if (cancelDialogError) {
        <div id="release-cancel-error" class="cancel-dialog-error" role="alert">{{ cancelDialogError }}</div>
      }
    </div>
  </section>
  <ng-template pTemplate="footer">
    <div class="master-dialog-footer">
      <span class="footer-spacer"></span>
      <p-button label="Keep Release" icon="pi pi-times" severity="secondary" [outlined]="true"
        [disabled]="isCancelling" (onClick)="cancelDialogVisible = false"></p-button>
      <p-button label="Cancel Release Family" severity="danger" icon="pi pi-ban" [loading]="isCancelling"
        [disabled]="!cancellationImpact || !cancellationReason.trim()" (onClick)="cancelRelease()"></p-button>
    </div>
  </ng-template>
</p-dialog>
